import { Trash2 } from "lucide-react";
import "@app/App.css";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Form } from "@comps/ui/form";
import { Button } from "@comps/ui/button";
import { del } from "@utils/utils";
import { useState } from "react";
import { ObjectDto, ObjectInfos } from "@dto/objects";
import { useModelStore } from "src/store";
import { useTranslation } from "react-i18next";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ObjectMenuItem, useHover } from "@utils/twClasses";
import { BASE_URL } from "@utils/constants";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { translate } from "src/i18n/i18n";
import { toastError } from "@utils/toasts";

interface ObjectMenuDeleteItemProps {
  objectInfo: ObjectInfos;
  onObjectDelete: (object: ObjectDto) => void;
  closeMenu: () => void;
}

function ObjectMenuDeleteItem({ objectInfo, onObjectDelete, closeMenu }: ObjectMenuDeleteItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));

  const objectDeleted = (object: ObjectDto) => {
    onObjectDelete(object);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel() {
    setIsDialogOpen(false);
    closeMenu();
  }

  
  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(true);
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger className={TW_ObjectMenuItem + ` ${objectInfo === null ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Trash2 className="ObjectMenuItemIcon" />{t("object.button.delete.text")}</DialogTrigger>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()} onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t("object.dialog.delete.title")}</DialogTitle>
          <DialogDescription className="pt-4 pb-2">{t("object.dialog.delete.description")}</DialogDescription>
        </DialogHeader>
        {objectInfo && <ObjectDeleteForm initialObject={objectInfo.object} onObjectDelete={objectDeleted} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface ObjectDeleteFormProps {
  initialObject: ObjectDto;
  onObjectDelete: (object: ObjectDto) => void;
  onCancel: () => void;
}

function ObjectDeleteForm({ initialObject, onObjectDelete, onCancel }: ObjectDeleteFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const formSchema = z.object({})
  const form = useForm<z.infer<typeof formSchema>>({})
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  function onSubmit(values: z.infer<typeof formSchema>) {

    if (workspace === null) {
      console.error("No workspace selected");
      return;
    }

    del(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${initialObject._key}`, null).then(response => {
      if (isCompozzError(response)) {
        const cError = response as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
        onCancel();
        return;
      }
      onObjectDelete(initialObject);
    })
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={cancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default ObjectMenuDeleteItem;
